import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COUNTRIES,
  type LoginClient,
  type ProfileResponse,
  type UpdateUserProfileInput,
  type UserProfile,
} from '@grc/types';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';
import './profile.css';

const CLIENT_LABELS: Record<LoginClient, string> = {
  web: 'Web',
  mobile: 'Mobile app',
  desktop: 'Desktop app',
  unknown: 'Unknown',
};

const WORKING_PATTERNS: Array<{ value: UserProfile['workingPattern']; label: string }> = [
  { value: '', label: 'Not specified' },
  { value: 'remote', label: 'Remote — works from home' },
  { value: 'hybrid', label: 'Hybrid — home and office' },
  { value: 'office', label: 'Office-based' },
];

/** "Chrome on Windows"-style summary of a sign-in user agent. */
function describeUserAgent(ua: string): string {
  if (!ua) return '—';
  const browser = /edg\//i.test(ua)
    ? 'Edge'
    : /firefox\//i.test(ua)
      ? 'Firefox'
      : /chrome\//i.test(ua)
        ? 'Chrome'
        : /safari\//i.test(ua)
          ? 'Safari'
          : /electron/i.test(ua)
            ? 'Desktop app'
            : 'Browser';
  const os = /windows/i.test(ua)
    ? 'Windows'
    : /iphone|ipad|ios/i.test(ua)
      ? 'iOS'
      : /android/i.test(ua)
        ? 'Android'
        : /mac os x|macintosh/i.test(ua)
          ? 'macOS'
          : /linux/i.test(ua)
            ? 'Linux'
            : '';
  return os ? `${browser} on ${os}` : browser;
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function ProfilePage() {
  const { me, refresh } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  // Bust the avatar <img> cache after upload/remove.
  const [avatarVersion, setAvatarVersion] = useState(0);

  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiFetch<ProfileResponse>('/me/profile'),
  });

  const [form, setForm] = useState<Required<UpdateUserProfileInput>>({
    name: '',
    jobTitle: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postcode: '',
    country: '',
    workingPattern: '',
  });
  useEffect(() => {
    if (profile.data) {
      const p = profile.data.profile;
      setForm({
        name: profile.data.user.name,
        jobTitle: p.jobTitle,
        phone: p.phone,
        addressLine1: p.addressLine1,
        addressLine2: p.addressLine2,
        city: p.city,
        region: p.region,
        postcode: p.postcode,
        country: p.country,
        workingPattern: p.workingPattern,
      });
    }
  }, [profile.data]);

  const onError = (err: unknown) =>
    toast(err instanceof ApiRequestError ? err.message : 'Something went wrong', 'error');
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['profile'] });
    void refresh();
  };

  const save = useMutation({
    mutationFn: () => apiFetch<ProfileResponse>('/me/profile', { method: 'PUT', body: form }),
    onSuccess: () => {
      toast('Profile saved.', 'success');
      invalidate();
    },
    onError,
  });

  const uploadAvatar = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiFetch<UserProfile>('/me/avatar', { method: 'PUT', formData });
    },
    onSuccess: () => {
      toast('Avatar updated.', 'success');
      setAvatarVersion((v) => v + 1);
      invalidate();
    },
    onError,
  });

  const removeAvatar = useMutation({
    mutationFn: () => apiFetch<UserProfile>('/me/avatar', { method: 'DELETE' }),
    onSuccess: () => {
      toast('Avatar removed.', 'success');
      setAvatarVersion((v) => v + 1);
      invalidate();
    },
    onError,
  });

  if (profile.isLoading || !me) return <LoadingPane label="Loading your profile" />;
  const data = profile.data;
  if (!data) return null;

  const hasAvatar = data.profile.hasAvatar;
  const avatarSrc = `${apiUrl(`/users/${data.user.id}/avatar`)}?v=${avatarVersion}`;

  const set = (key: keyof UpdateUserProfileInput) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <div>
      <PageHeader title="My profile" subtitle="How you appear to your team, and your sign-in history" />

      <Card title="Avatar">
        <div className="avatar-row">
          <span className="avatar-preview" aria-hidden="true">
            {hasAvatar ? (
              <img src={avatarSrc} alt="" />
            ) : (
              <span className="avatar-initials">{initials(data.user.name)}</span>
            )}
          </span>
          <div className="avatar-actions">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg"
              className="sr-only"
              aria-label="Choose an avatar image"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadAvatar.mutate(file);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              loading={uploadAvatar.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {hasAvatar ? 'Replace avatar' : 'Upload avatar'}
            </Button>
            {hasAvatar && (
              <Button
                variant="ghost"
                loading={removeAvatar.isPending}
                onClick={() => removeAvatar.mutate()}
              >
                Remove
              </Button>
            )}
            <p className="avatar-hint">PNG or JPEG, up to 50&nbsp;MB.</p>
          </div>
        </div>
      </Card>

      <form onSubmit={submit} className="profile-form">
        <Card title="About you">
          <div className="profile-grid">
            <Field label="Full name" required>
              {(props) => (
                <Input {...props} required value={form.name} onChange={(e) => set('name')(e.target.value)} />
              )}
            </Field>
            <Field label="Job title">
              {(props) => (
                <Input {...props} value={form.jobTitle} onChange={(e) => set('jobTitle')(e.target.value)} />
              )}
            </Field>
            <Field label="Phone">
              {(props) => (
                <Input {...props} type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set('phone')(e.target.value)} />
              )}
            </Field>
            <Field label="Email">
              {(props) => <Input {...props} value={data.user.email} disabled />}
            </Field>
          </div>
        </Card>

        <Card title="Home address & working pattern">
          <p className="profile-note">
            Your home address and usual working location help your organisation evidence remote and
            hybrid working arrangements in assessments.
          </p>
          <div className="profile-grid">
            <Field label="Address line 1">
              {(props) => (
                <Input {...props} autoComplete="address-line1" value={form.addressLine1} onChange={(e) => set('addressLine1')(e.target.value)} />
              )}
            </Field>
            <Field label="Address line 2">
              {(props) => (
                <Input {...props} autoComplete="address-line2" value={form.addressLine2} onChange={(e) => set('addressLine2')(e.target.value)} />
              )}
            </Field>
            <Field label="Town / city">
              {(props) => (
                <Input {...props} autoComplete="address-level2" value={form.city} onChange={(e) => set('city')(e.target.value)} />
              )}
            </Field>
            <Field label="County / region">
              {(props) => (
                <Input {...props} value={form.region} onChange={(e) => set('region')(e.target.value)} />
              )}
            </Field>
            <Field label="Postcode">
              {(props) => (
                <Input {...props} autoComplete="postal-code" value={form.postcode} onChange={(e) => set('postcode')(e.target.value)} />
              )}
            </Field>
            <Field label="Country">
              {(props) => (
                <Select {...props} value={form.country} onChange={(e) => set('country')(e.target.value)}>
                  <option value="">Select…</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Usual working location">
              {(props) => (
                <Select
                  {...props}
                  value={form.workingPattern}
                  onChange={(e) => set('workingPattern')(e.target.value)}
                >
                  {WORKING_PATTERNS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </Card>

        <div className="profile-save">
          <Button type="submit" loading={save.isPending}>
            Save profile
          </Button>
        </div>
      </form>

      <Card title="Where you've signed in">
        {data.clientsUsed.length > 0 && (
          <p className="profile-note">
            You have signed in from:{' '}
            <span className="client-badges">
              {data.clientsUsed.map((client) => (
                <Badge key={client} tone={client === 'unknown' ? 'neutral' : 'info'}>
                  {CLIENT_LABELS[client]}
                </Badge>
              ))}
            </span>
          </p>
        )}
        {data.recentLogins.length === 0 ? (
          <p className="profile-note">No sign-ins recorded yet — history builds from your next sign-in.</p>
        ) : (
          <Table
            head={
              <>
                <th>When</th>
                <th>From</th>
                <th>Device</th>
                <th>IP address</th>
              </>
            }
          >
            {data.recentLogins.map((login) => (
              <tr key={login.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(login.createdAt)}</td>
                <td>
                  <Badge tone={login.client === 'unknown' ? 'neutral' : 'info'}>
                    {CLIENT_LABELS[login.client]}
                  </Badge>
                </td>
                <td>{describeUserAgent(login.userAgent)}</td>
                <td>{login.ip ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
